import React, { useEffect, useState } from 'react';
import { useApi } from '../../context/ApiContext';

export default function UsersAdmin(){
  const { adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser } = useApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorInfo, setErrorInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const [form, setForm] = useState({ name:'', email:'', password:'', role:'agent' });
  const [creating, setCreating] = useState(false);

  async function refresh(){
    try {
      setLoading(true);
      setError('');
      setErrorInfo(null);
      const list = await adminListUsers();
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) { setError(e?.message || 'Failed to load users'); setErrorInfo({ status: e?.status || null, body: e?.body ?? null }); }
    finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);

  async function create(){
    if (!form.email || !form.password) { alert('Email and password required'); return; }
    try {
      setCreating(true);
      await adminCreateUser(form);
      setForm({ name:'', email:'', password:'', role:'agent' });
      await refresh();
    } catch (e) { alert((e?.status ? `[${e.status}] `: '') + (e?.message || 'Failed to create user')); }
    finally { setCreating(false); }
  }

  async function toggleActive(u){
    try { await adminUpdateUser(u.id, { isActive: !u.isActive }); await refresh(); } catch (e) { alert((e?.status ? `[${e.status}] `: '') + (e?.message || 'Failed')); }
  }

  async function remove(u){
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await adminDeleteUser(u.id); await refresh(); } catch(e){ alert((e?.status ? `[${e.status}] `: '') + (e?.message || 'Failed')); }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Users</h1>
        <p className="text-gray-600">Manage admin and agent accounts.</p>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <h2 className="font-semibold mb-3">Create User</h2>
        <div className="grid md:grid-cols-5 gap-2 items-end">
          <input className="border rounded px-3 py-2" placeholder="Name" value={form.name} onChange={(e)=>setForm(s=>({...s,name:e.target.value}))} />
          <input className="border rounded px-3 py-2" placeholder="Email" value={form.email} onChange={(e)=>setForm(s=>({...s,email:e.target.value}))} />
          <input type="password" className="border rounded px-3 py-2" placeholder="Password" value={form.password} onChange={(e)=>setForm(s=>({...s,password:e.target.value}))} />
          <select className="border rounded px-3 py-2" value={form.role} onChange={(e)=>setForm(s=>({...s,role:e.target.value}))}>
            <option value="agent">Agent</option>
            <option value="admin">Admin (Super)</option>
          </select>
          <button className="px-3 py-2 border rounded" disabled={creating} onClick={create}>{creating ? 'Creating…' : 'Create'}</button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button className="text-xs underline" onClick={()=>setShowDebug((s)=>!s)}>{showDebug ? 'Hide details' : 'Show details'}</button>
          </div>
          {showDebug && (
            <pre className="mt-2 text-xs bg-white/60 text-gray-800 p-2 rounded overflow-auto max-h-48 border">
{JSON.stringify({ status: errorInfo?.status ?? null, body: errorInfo?.body ?? null }, null, 2)}
            </pre>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 && (
                <tr className="border-t"><td className="px-3 py-3" colSpan={5}>Loading…</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id} className="border-t">
                  <td className="px-3 py-2">{u.name || '-'}</td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2 capitalize">{u.role}</td>
                  <td className="px-3 py-2">{u.isActive ? 'Active' : 'Disabled'}</td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button className="px-2 py-1 border rounded" onClick={()=>toggleActive(u)}>{u.isActive ? 'Disable' : 'Enable'}</button>
                    <button className="px-2 py-1 border rounded" onClick={()=>remove(u)}>Delete</button>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr className="border-t"><td className="px-3 py-8 text-center text-gray-600" colSpan={5}>No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
